/**
 * Debounced Queue
 * A queue class that gathers and delays execution of multiple tasks tied to a single queue.
 *
 */
class DebouncedQueue {
	/**
	 * Create a queue
	 * @param {object} options
	 *	threshold - time to delay execution after last event added to queue
	 *	onQueueInitialize - lifecycle hook for the initialization of a new timed queue
	 */
	constructor(options = {}) {
		this.queue = [];
		this.timeoutId = null;
		this.threshhold = options.threshhold || 1000;
		this.onQueueInitialize = options.onQueueInitialize || null;
		this.onQueueClear = options.onQueueClear || null;
	}

	/** Set threshold or onQueueInitialize through options
	 * @param {object} options
	 */
	setOptions(options) {
		this.threshhold = options.threshhold || this.threshhold;
		this.onQueueInitialize = options.onQueueInitialize || this.onQueueInitialize;
		this.onQueueClear = options.onQueueClear || this.onQueueClear;
	}

	/** Add an action to the queue
	 * @param {function} f - the action to wrap in the queue
	 * @return {function} debounced action tied to the queue
	 */
	addToQueue(f) {
		var self = this;
		return function () {
			var args = [this].concat(Array.prototype.slice.call(arguments, 0));

			if (self.queue.length === 0 && self.onQueueInitialize) {
				self.onQueueInitialize();
			}

			if (self.queue) {
				self.queue.push(f.bind.apply(f, args));
			} else {
				self.queue = [f.bind.apply(f, args)];
			}

			if (self.timeoutId) {
				clearTimeout(self.timeoutId);
			}

			self.timeoutId = setTimeout(function () {
				// simple for loop, as Array.forEach doesn't take into
				// account items that are added to the queue
				// while we're looping through it.
				for (let i = 0; i < self.queue.length; i++) {
					self.queue[i]();
				}

				self.queue = [];
				self.timeoutId = null;
				if (self.onQueueClear) {
					self.onQueueClear();
				}
			}, self.threshhold);
		};
	}
}

module.exports = DebouncedQueue;
